import { useState, useEffect, useCallback } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useIsMobile } from "./hooks/useIsMobile";
import { ToastProvider, useToast } from "./context/ToastContext";
import Landing from "./pages/Landing";
import Header from "./components/Header";
import MissionMapLayout from "./components/MissionMapLayout";
import PracticeScreen from "./components/PracticeScreen";
import MissionComplete from "./components/MissionComplete";
import Dashboard from "./components/Dashboard";
import WordList from "./components/WordList";
import api from "./api/axios";
import { getMissionProgress, getMissionLevel } from "./data/vocab";

function Spinner({ size = 32 }) {
  return <div style={{ width: size, height: size, border: "3px solid #e8e2d8", borderTop: "3px solid #1a8f68", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />;
}

function AuthGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f0e8" }}>
        <Spinner size={36} />
      </div>
    );
  }
  if (!user) return <Landing />;
  return children;
}

function CompletedMissionView({ mission, wordStates, onBack, onRepractice }) {
  const prog = getMissionProgress(mission, wordStates);
  const now = Date.now() / 3600000;

  return (
    <div style={{ maxWidth: 520, margin: "0 auto", animation: "popIn 0.25s ease both" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderRadius: "18px 18px 0 0", padding: "0 24px", height: 58, display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #ede8df", borderBottom: "none" }}>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#b0a898", cursor: "pointer", background: "none", border: "none", fontFamily: "inherit" }}>
          <svg width="14" height="14" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M9.5 3L5 7.5l4.5 4.5"/></svg>
          Back
        </button>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>{mission.title}</div>
        <div style={{ width: 60 }} />
      </div>

      <div style={{ height: 6, background: "#1a8f68", border: "1px solid #ede8df", borderTop: "none", borderBottom: "none" }} />

      <div style={{ background: "#fff", borderRadius: "0 0 18px 18px", padding: "36px 32px 32px", border: "1px solid #ede8df", borderTop: "none" }}>
        {/* Trophy */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 28 }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#e8f7f1", border: "3px solid #1a8f68", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#1a8f68" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 21h8M12 17v4M17 4h2a2 2 0 012 2v1a5 5 0 01-5 5h-1M7 4H5a2 2 0 00-2 2v1a5 5 0 005 5h1"/>
              <path d="M7 4h10v7a5 5 0 01-10 0V4z"/>
            </svg>
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#111", marginBottom: 6, letterSpacing: -0.4 }}>Mission Complete!</div>
          <div style={{ fontSize: 13, color: "#b0a898", textAlign: "center" }}>
            You've already mastered all {prog.total} words in <strong style={{ color: "#111" }}>{mission.title}</strong>.
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 24 }}>
          <div style={{ background: "#f5f0e8", borderRadius: 13, padding: "14px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#1a8f68" }}>{prog.reviewed}</div>
            <div style={{ fontSize: 11, color: "#b0a898", fontWeight: 600 }}>Words learned</div>
          </div>
          <div style={{ background: "#f5f0e8", borderRadius: 13, padding: "14px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#1a8f68" }}>{prog.pct}%</div>
            <div style={{ fontSize: 11, color: "#b0a898", fontWeight: 600 }}>Completion</div>
          </div>
        </div>

        {/* Subtitle */}
        <div style={{ background: "#e8f7f1", borderRadius: 12, padding: "12px 16px", marginBottom: 24, fontSize: 13, color: "#0a6644", fontWeight: 600, textAlign: "center" }}>
          Practicing again helps you retain these words in long-term memory.
        </div>

        {/* Buttons */}
        <button onClick={onRepractice} style={{ width: "100%", padding: "15px", background: "#1a8f68", color: "#fff", border: "none", borderRadius: 13, fontSize: 15, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 10 }}>
          Practice Again
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
        </button>
        <button onClick={onBack} style={{ width: "100%", padding: "13px", background: "transparent", color: "#b0a898", border: "1.5px solid #e8e2d8", borderRadius: 13, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          Back to Missions
        </button>
      </div>
    </div>
  );
}

function AppInner() {
  const isMobile = useIsMobile();
  const { user, updateUser } = useAuth();
  const toast = useToast();
  const [wordStates, setWordStates] = useState({});
  const [sessions, setSessions] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [tab, setTab] = useState("map");
  const [view, setView] = useState("map");
  const [activeMission, setActiveMission] = useState(null);
  const [streak, setStreak] = useState(0);
  const [sessionXP, setSessionXP] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [questionsNeeded, setQuestionsNeeded] = useState(20);
  const [sessionStartOffset, setSessionStartOffset] = useState(0);
  const [levelUpMsg, setLevelUpMsg] = useState(null);

  const level = getMissionLevel(wordStates);

  useEffect(() => {
    api.get("/progress")
      .then(res => {
        // Normalize: backend stores wordId (number) but frontend needs id (number) for vocab lookup
        const raw = res.data.wordStates || {};
        const normalized = {};
        Object.entries(raw).forEach(([key, val]) => {
          normalized[key] = { ...val, id: Number(val.wordId ?? key) };
        });
        setWordStates(normalized);
        setSessions(res.data.sessions || []);
      })
      .catch(() => toast.error("Could not load your progress."))
      .finally(() => setDataLoading(false));
  }, []);

  // Start actual practice session (bypasses the completed-mission check)
  const startPractice = useCallback((mission) => {
    const isCustom = String(mission.id).startsWith("custom-");
    const alreadyDone = isCustom ? 0 : mission.wordIds.filter(id => wordStates[id]?.lastReviewTime > 0).length;
    const allDone = !isCustom && alreadyDone >= mission.wordIds.length;
    const offset = (isCustom || allDone) ? 0 : alreadyDone;
    const remaining = (isCustom || allDone) ? 20 : Math.max(1, 20 - alreadyDone);
    setActiveMission(mission);
    setView("practice");
    setTab("learn");
    setSessionXP(0);
    setAnsweredCount(0);
    setQuestionsNeeded(remaining);
    setSessionStartOffset(offset);
    setStreak(0);
  }, [wordStates]);

  // Called when user clicks a mission card
  const startMission = useCallback((mission) => {
    const prog = getMissionProgress(mission, wordStates);
    const isComplete = prog.reviewed >= prog.total;
    if (isComplete) {
      // Show the completed mission info view instead of jumping straight to practice
      setActiveMission(mission);
      setView("completed-view");
      setTab("learn");
    } else {
      startPractice(mission);
    }
  }, [wordStates, startPractice]);

  // For clicking memory breakdown rows in stats OR lexicon words
  const startFilteredPractice = useCallback((label, ids, forcedFirstId = null) => {
    if (!ids || ids.length === 0) return;
    const customMission = {
      id: `custom-${Date.now()}`,
      title: label,
      subtitle: `Practicing ${ids.length} word${ids.length > 1 ? "s" : ""}`,
      color: "#1a8f68",
      wordIds: ids,
      forcedFirstId,
    };
    startPractice(customMission);
  }, [startPractice]);

  const handleAnswer = useCallback(async (wordId, correct, newHalfLife, xpEarned) => {
    const prevLevel = getMissionLevel(wordStates);
    setWordStates(prev => {
      const numId = Number(wordId);
      const ex = prev[wordId] || { id: numId, correctCount: 0, incorrectCount: 0, lastReviewTime: 0, halfLife: 1.0 };
      const newStates = { ...prev, [wordId]: { ...ex, id: numId, correctCount: ex.correctCount + (correct ? 1 : 0), incorrectCount: ex.incorrectCount + (correct ? 0 : 1), lastReviewTime: Date.now() / 3600000, halfLife: newHalfLife } };
      const newLevel = getMissionLevel(newStates);
      if (newLevel > prevLevel) {
        setTimeout(() => {
          setLevelUpMsg(newLevel);
          setTimeout(() => setLevelUpMsg(null), 3500);
          toast.success(`Mission complete! You reached Level ${newLevel}!`);
        }, 400);
      }
      return newStates;
    });
    setStreak(p => correct ? p + 1 : 0);
    setSessionXP(p => p + (xpEarned || 0));
    setAnsweredCount(p => {
      const next = p + 1;
      if (next >= questionsNeeded) setTimeout(() => setView("complete"), 350);
      return next;
    });
    try {
      const res = await api.post("/progress/answer", { wordId, correct, newHalfLife, xpEarned });
      if (res.data.user) updateUser(res.data.user);
    } catch {}
  }, [user, updateUser, toast, questionsNeeded]);

  const handleReset = useCallback(async () => {
    try {
      const res = await api.post("/progress/reset");
      setWordStates({}); setSessions([]); setStreak(0); setSessionXP(0);
      setView("map"); setTab("map");
      if (res.data.user) updateUser(res.data.user);
    } catch { toast.error("Reset failed."); }
  }, [updateUser, toast]);

  const handleTab = useCallback((t) => { setTab(t); if (t !== "learn") setView(t); }, []);

  const backToMap = useCallback(() => { setView("map"); setTab("map"); }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#f5f0e8" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes popIn { from { opacity:0; transform:scale(0.92); } to { opacity:1; transform:scale(1); } }
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
        @keyframes slideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        .animate-popIn { animation: popIn 0.25s ease both; }
        .animate-fadeIn { animation: fadeIn 0.2s ease both; }
        body, html { background: #f5f0e8 !important; margin: 0; padding: 0; }
        * { box-sizing: border-box; }
      `}</style>

      {levelUpMsg && (
        <div style={{ position: "fixed", top: 24, left: "50%", transform: "translateX(-50%)", background: "#1a8f68", color: "#fff", borderRadius: 16, padding: "13px 28px", zIndex: 9999, boxShadow: "0 8px 32px rgba(26,143,104,0.35)", fontWeight: 700, fontSize: 15, whiteSpace: "nowrap" }}>
          Mission complete! You reached Level {levelUpMsg}
        </div>
      )}

      <Header tab={tab} onTab={handleTab} streak={streak} onReset={handleReset} level={level} />

      <main style={{ width: "100%", padding: isMobile ? "16px 14px 90px" : "32px 4vw 64px" }}>
        {(tab === "map" || tab === "learn") && view === "map" && (
          <MissionMapLayout
            wordStates={wordStates}
            sessions={sessions}
            onStart={startMission}
            onPractice={startFilteredPractice}
            loading={dataLoading}
            isMobile={isMobile}
          />
        )}
        {tab === "learn" && view === "completed-view" && activeMission && (
          <CompletedMissionView
            mission={activeMission}
            wordStates={wordStates}
            onBack={backToMap}
            onRepractice={() => startPractice(activeMission)}
          />
        )}
        {tab === "learn" && view === "practice" && activeMission && (
          <PracticeScreen mission={activeMission} wordStates={wordStates} onAnswer={handleAnswer} onExit={backToMap} streak={streak} sessionXP={sessionXP} answeredCount={answeredCount} startOffset={sessionStartOffset} />
        )}
        {tab === "learn" && view === "complete" && activeMission && (
          <MissionComplete
            mission={activeMission}
            xpEarned={sessionXP}
            onBack={String(activeMission.id).startsWith("custom-") ? () => { setView("words"); setTab("words"); } : backToMap}
            onNext={(next) => startPractice(next)}
          />
        )}
        {tab === "stats" && (
          <Dashboard wordStates={wordStates} sessions={sessions} loading={dataLoading} onPracticeFiltered={startFilteredPractice} />
        )}
        {tab === "words" && (
          <WordList wordStates={wordStates} loading={dataLoading} onPractice={startFilteredPractice} onTabSwitch={() => setTab("learn")} />
        )}
      </main>
    </div>
  );
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <ToastProvider>
          <AuthGate>
            <AppInner />
          </AuthGate>
        </ToastProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}
